import { BET_KEY_LABELS, SIDE_NAMES } from '@hit-game/shared/constants';
import { AccountSide, type GameBetKey } from '@hit-game/shared/enums';

import { formatMoney } from '../../utils/format';
import { FIELD_CLASS } from '../common/form.styles';
import { gridAmount, type DoorGrid } from './group.form';

/**
 * Các ô nhập của hộp thoại sửa nhóm.
 *
 * Tách khỏi `GroupConfigModal` vì chúng thuần trình bày — không biết gì về nhóm,
 * proxy hay lượt lưu — nên trộn chung chỉ làm file điều phối dài ra.
 */

interface SegmentTabProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/** Dải nút chọn-một. Ít lựa chọn và cần thấy hết cùng lúc thì hơn hẳn dropdown. */
export function SegmentTab({ options, value, onChange, disabled = false }: SegmentTabProps) {
  return (
    <div className="border-line bg-canvas inline-flex rounded-lg border p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => {
            onChange(option.value);
          }}
          className={`rounded-md px-3 py-1.5 text-[12px] font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
            option.value === value
              ? 'bg-accent text-white shadow-sm'
              : 'text-ink-dim hover:text-ink'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Lưới tiền của cả nhóm: hàng = cửa của game, hai cột = Phe A / Phe B, mỗi ô một số tiền.
 *
 * Thay cho hai ô "tiền cả phe" cũ: cửa của mỗi phe không còn cố định theo game mà do người
 * dùng khai. Phe được random lại mỗi ván cho 2 trong 4 tài khoản nên không có tài khoản nào để
 * gắn cửa vào — engine cộng các cửa của phe lại rồi tự rải cho 2 tài khoản của phe đó.
 * Ô để trống = 0 = không đánh cửa đó; cùng một cửa được phép có tiền ở cả hai phe.
 */
export function SideDoorGrid({
  keys,
  grid,
  minCoin,
  disabled,
  onChange,
}: {
  keys: readonly GameBetKey[];
  grid: DoorGrid;
  /** Mệnh giá nhỏ nhất — ô nào có tiền mà không phải bội số thì viền cảnh báo. */
  minCoin: number;
  disabled: boolean;
  onChange: (side: AccountSide, key: GameBetKey, amount: number) => void;
}) {
  const sides = Object.values(AccountSide);
  const totalOf = (side: AccountSide) =>
    keys.reduce((sum, key) => sum + gridAmount(grid, side, key), 0);

  return (
    <div className="border-line overflow-hidden rounded-xl border">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="bg-canvas text-ink-dim text-[10px] font-bold uppercase tracking-wider">
            <th className="px-3 py-2 text-left">Cửa</th>
            {sides.map((side) => (
              <th key={side} className="px-3 py-2 text-right">
                {SIDE_NAMES[side]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => (
            <tr key={key} className="border-line border-t">
              <td className="text-ink px-3 py-1.5 font-semibold">{BET_KEY_LABELS[key]}</td>
              {sides.map((side) => {
                const amount = gridAmount(grid, side, key);
                const inexact = amount > 0 && amount % minCoin !== 0;
                return (
                  <td key={side} className="px-2 py-1">
                    <input
                      type="text"
                      inputMode="numeric"
                      aria-label={`${BET_KEY_LABELS[key]} — ${SIDE_NAMES[side]}`}
                      // Hiện có dấu phân cách: "400.000" đọc ra ngay, "400000" phải đếm số 0.
                      value={amount > 0 ? formatMoney(amount) : ''}
                      onChange={(e) => {
                        onChange(side, key, Number(e.target.value.replace(/\D/g, '')) || 0);
                      }}
                      disabled={disabled}
                      placeholder="0"
                      title={inexact ? `Phải là bội số của ${formatMoney(minCoin)}` : undefined}
                      className={`${FIELD_CLASS} text-right font-mono font-bold ${
                        inexact ? 'border-warning' : ''
                      }`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-line bg-canvas border-t text-[12px] font-bold">
            <td className="text-ink-dim px-3 py-2 uppercase tracking-wider">Tổng</td>
            {sides.map((side) => (
              <td key={side} className="text-ink px-3 py-2 text-right font-mono">
                {formatMoney(totalOf(side))}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
